import { replace, split } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { SRP } from '../../common/analytics/sourceTypes';
import config from '../../common/config/index';
import { locationTypeMappings } from '../../common/helpers/constants';
import { getSrpSeoData } from '../../common/helpers/ui/pageTitleMetaDescription';
import { parseSrpParams } from '../../common/helpers/routing/srpUrlParser';
import { titleGenerator } from '../../common/helpers/ui/srpTitleHelper';
import {
  DisclaimerContainer,
  Divider,
  TopContentWrapper,
  Wrapper,
} from '../../components/pages/searchResultPage/SearchResultPage.style';
import ProjectListContainer, {
  PER_PAGE,
} from '../../components/searchResultPage/searchFilter/ProjectListContainer';
import SearchFilter from '../../components/searchResultPage/searchFilter/SearchFilter';
import SrpBreadcrumb from '../../components/searchResultPage/srpBreadcrumb/SrpBreadcrumb';
import SrpSchema from '../../components/searchResultPage/srpSchema/SrpSchema';
import { translateSearchType } from '../../components/searchResultPage/advanceSearchUtil';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import SeoHeader from '../../components/seoHeader/SeoHeader';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
//TODO: move to service layer
import {
  SEARCH_PAGE_LOCATION_NAME_QUERY,
  SearchPageLocationNameQueryData,
  SearchPageLocationNameQueryVars,
} from '../../queries/searchPage/searchProjects';
import { projectManager } from '../../services/projectManager';
import {
  updateCurrentKeyword,
  updateCurrentSourcePage,
  updateLoadingProjectCarousel,
  updateProjectsItems,
  updateProjectTotalCount,
} from '../../store/search/slice';
import { WholeState as SearchState } from '../../store/search/type';
import {
  getAllPropertyTypeValues,
  PropertyType,
} from '../../common/helpers/domain/propertyTypes';
import { isCommercialBuildingLandingPage } from '../../common/helpers/routing/srpRoutesHelper';
import commonResource from '../../locales/th/common.json';
import { ProjectItem } from '../../queries/fragments/project';
import { Disclaimer } from '../../components/disclaimer/Disclaimer';
import { getSrpTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import Row from '../../components/rows/Row';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import searchResource from '../../locales/th/search.json';
import { DynamicComponentForArticleFeedModule } from '../../components/pages/homePage/DynamicComponents';
import {
  trackSrpArticle,
  trackSrpArticleFeedShowMore,
  trackSrpArticleFeedTab,
} from '../../common/analytics/analyticsTracker';
import { WholeState } from '../../store/common/type';
import { fetchLocation } from '../../services/location';
import { AdUnits } from '../../common/helpers/ads/dfp';
import {
  getAdSectByPageType,
  getAdSubsectByPropertyType,
} from '../../common/helpers/ads/AdsHelper';
import { LandingPageTypes } from '../../common/helpers/domain/landingPages';

const TYPE_PROJECT_EN = 'project';
const TYPE_PROJECT_THAI = 'โครงการ';
const isProjectType = (type: string) =>
  type === TYPE_PROJECT_EN || type === TYPE_PROJECT_THAI;

interface SearchResultPageProps {
  searchParams: SearchParams;
  searchTitle: string;
  freeText: string;
  slug: string;
  type: string;
  parent: string;
  advanceSearchQueries: AdvanceSearchQueries;
  canonicalLink: string;
  srpBlurb: string;
}

function SearchResultPage({
  freeText,
  type,
  slug,
  parent,
  advanceSearchQueries,
  searchParams,
  searchTitle,
  canonicalLink,
  srpBlurb,
}: SearchResultPageProps): React.ReactElement {
  const loading = useSelector((state: SearchState) =>
    state.search !== undefined ? state.search.loadingProjectsCarousel : false,
  );

  const totalCount = useSelector((state: SearchState) =>
    state.search !== undefined ? state.search.projectsTotalCount : 0,
  );

  const projectItems = useSelector((state: SearchState) =>
    state.search !== undefined ? state.search.projectsItems : [],
  );

  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  const advanceFilterParameter = {
    ...advanceSearchQueries,
    type,
    slug,
    parent,
    freeText,
    q: freeText,
  };

  const isShowNoResult = !loading && (totalCount === 0 || totalCount === null);
  const propertyTypeInTh = getAllPropertyTypeValues(
    advanceFilterParameter.propertyType,
  );

  const data = getSrpSeoData({
    freeText,
    advanceSearchQueries,
    canonicalLink,
    isShowNoResult,
    searchParams,
    parent,
  });

  const generatedTitle = titleGenerator({
    searchTitle,
    termsTaxonomy: searchParams.termsTaxonomy,
    freeText,
    propertyTypeInTh,
    parent,
  });

  const titleText = searchResource.contentList.articlesAndVideos;
  const analyticsSource = SRP.PAGE;

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <SrpSchema searchTitle={searchTitle} />
      <SeoHeader
        title={data.seoTitle}
        description={data.description}
        canonicalLink={data.canonicalLink}
        imageUrl={data.sharpieImgUri}
      />
      <SrpBreadcrumb
        type={type}
        slug={slug}
        searchTitle={searchTitle}
        projectItems={projectItems}
      />
      <TopContentWrapper>
        <Wrapper>
          <SearchFilter
            filterParams={advanceFilterParameter}
            title={generatedTitle}
            srpBlurb={srpBlurb}
          />
          <ProjectListContainer
            advanceSearchQueries={{
              ...advanceFilterParameter,
              ...searchParams,
            }}
          />
        </Wrapper>
      </TopContentWrapper>
      {totalCount > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle title={titleText} />
          <DynamicComponentForArticleFeedModule
            excludePostIds={[]}
            tabDefinition={getSrpTabDefinition(searchParams, freeText)}
            adUnit={AdUnits.SRP_MrecInfiniteAd}
            adSect={
              !advanceFilterParameter.propertyType
                ? getAdSectByPageType(LandingPageTypes.SearchResultPage)
                : getAdSubsectByPropertyType(
                    advanceFilterParameter.propertyType,
                  )
            }
            onClickPostCard={(post) =>
              trackSrpArticle(userInfo, post, analyticsSource)
            }
            onClickArticleFeedShowMore={() => {
              trackSrpArticleFeedShowMore(userInfo, analyticsSource);
            }}
            onClickArticleFeedTab={(selectedTab) => {
              trackSrpArticleFeedTab(userInfo, analyticsSource, selectedTab);
            }}
          />
        </Row>
      )}
      <Divider />
      <DisclaimerContainer>
        <Disclaimer />
      </DisclaimerContainer>
      <SeoFooter />
    </Page>
  );
}

SearchResultPage.getInitialProps = async ({
  store,
  query,
  asPath,
  apolloClient,
}: CustomNextPageContext) => {
  const srpParams = parseSrpParams(query);
  const canonicalLink = isCommercialBuildingLandingPage(srpParams)
    ? config.baseUrl + `${commonResource.propertyTypes.commercial}/`
    : config.baseUrl + split(asPath, '?')[0].substring(1);

  // Data manipulation
  const { type, slug, q, minPrice, maxPrice, propertyType, parent } = srpParams;
  const freeText = q;
  let searchTitle = '';
  const advanceSearchQueries = {
    minPrice,
    propertyType,
    maxPrice,
  };
  const typeEn = translateSearchType(type);

  // Setting search data in store
  store.dispatch(updateCurrentSourcePage(SRP.PAGE));
  store.dispatch(updateCurrentKeyword(freeText));

  // Setting search parameters based on project type
  let searchParams: SearchParams;

  if (isProjectType(type)) {
    searchParams = {
      termsTaxonomy: '',
      termsSlug: '',
      slug: slug as string,
      projectSlug: slug as string,
    };
  } else {
    searchParams = {
      termsTaxonomy: typeEn,
      termsSlug: slug as string,
      slug: '',
      projectSlug: '',
    };
  }

  const advanceFilterParameter = {
    ...advanceSearchQueries,
    type,
    slug,
    parent,
    freeText,
    q: freeText,
  };
  let projectItems: ProjectItem[] = [];
  let totalCount = 0;
  try {
    store.dispatch(updateLoadingProjectCarousel(true));
    store.dispatch(updateProjectsItems([]));
    store.dispatch(updateProjectTotalCount(0));

    const projectItemsData = await projectManager(
      0,
      { ...advanceFilterParameter, ...searchParams },
      apolloClient,
      PER_PAGE,
    );
    projectItems = projectItemsData.items;
    totalCount = projectItemsData.totalCount;

    //TODO: move to service layer
    const locationQueryResponse = await apolloClient.query<
      SearchPageLocationNameQueryData,
      SearchPageLocationNameQueryVars
    >({
      query: SEARCH_PAGE_LOCATION_NAME_QUERY,
      variables: {
        slug,
        termTaxonomy: locationTypeMappings.find((x) => x.th === type)?.en,
      },
    });
    const locationName = locationQueryResponse?.data?.result?.name;
    searchTitle = freeText
      ? freeText
      : locationName || replace(slug as string, /-/g, ' ');
  } catch (e) {
    console.log('Error when fetch project items:', e);
  }

  store.dispatch(updateProjectsItems(projectItems));
  store.dispatch(updateProjectTotalCount(totalCount));
  store.dispatch(updateLoadingProjectCarousel(false));

  let srpBlurb;
  const locationType =
    type && locationTypeMappings.find((x) => x.th === type)?.en;
  if (locationType) {
    const [locationInfo] = await Promise.all([
      fetchLocation(apolloClient, {
        locationType: locationType,
        locationSlug: slug,
      }),
    ]);
    srpBlurb = locationInfo?.locationBlurb;
  }

  return {
    searchParams,
    type,
    slug,
    searchTitle,
    freeText,
    parent,
    advanceSearchQueries,
    canonicalLink,
    srpBlurb,
  };
};

export interface SearchParams {
  termsTaxonomy: string;
  termsSlug: string;
  slug: string;
  projectSlug: string;
}

export interface AdvanceSearchQueries {
  minPrice: string;
  propertyType: PropertyType;
  maxPrice: string;
}

export default SearchResultPage;
